import { functions } from "@/services/functions";
import { Timer } from "@/types/v1";
import {
  AdminReassignShopOwnerRequest,
  AdminReassignShopOwnerResult,
  AuthUser,
  EmployeeRole,
  Integrators,
  CustomerUpdate,
  Shop,
  StripePromotionCode,
  CustomerUpdateTransition,
  CustomerUpdateMedium,
} from "@/types/v2";
import { httpsCallable } from "firebase/functions";
import { sentry } from "@/services/sentry";
import { SPAN_CAPTURE_RATE } from "@/lib/const";

type integratorCallableData = {
  shopId: string;
  integrator: Integrators;
  apiToken?: string;
  apiUrl?: string;
  apiPartnerId?: string;
  apiTenantId?: string;
};

interface UpdateCustomerUpdateData {
  shopId: string;
  customerUpdateId?: string;
  transition: CustomerUpdateTransition;
  comments?: string;
  updateMedium?: CustomerUpdateMedium[];
  userId?: string;
  cardId?: string;
  desiredUpdateTime?: Date;
  skipNextUpdate?: boolean;
}

type UpdateCustomerUpdateResponse =
  | {
      data: CustomerUpdate;
      error?: undefined;
      success: boolean;
    }
  | {
      data?: undefined;
      error: string;
      success: false;
    };

export const httpsCallables = {
  updateShopTimers: (shopId: string, timerId: string, timerData?: Timer) => {
    const callable = httpsCallable(functions, "updateShopTimer");
    if (timerData) {
      return callable({ shopId, timerId, timerData }).then(() =>
        console.log("Finished")
      );
    }
    return callable({ shopId, timerId }).then(() => console.log("Finished"));
  },
  requestSyncWithSource: ({
    shopId,
    syncType,
    force,
  }: {
    shopId: string;
    syncType?: "incremental" | "full" | "fullReSync";
    force?: boolean;
  }) => {
    const callable = httpsCallable<
      { shopId: string; syncType?: string; force?: boolean },
      { queued: boolean; reason?: string }
    >(functions, "integrations-requestSync");
    return callable({ shopId, syncType, force });
  },
  syncWithSource: ({
    shopId,
    integrator,
    apiToken,
    apiUrl,
    fullSync,
    fullReSync,
    apiPartnerId,
    apiTenantId,
  }: integratorCallableData & {
    fullSync?: boolean;
    fullReSync?: boolean;
  }) => {
    const callable = httpsCallable(functions, "integrations-sync");
    const fn = callable({
      shopId,
      integrator,
      apiToken,
      apiUrl,
      fullSync,
      fullReSync,
      apiPartnerId,
      apiTenantId,
    });

    // Use random number to determine if to log the span
    if (Math.random() < SPAN_CAPTURE_RATE) {
      return sentry.startSpan(
        {
          name: "Sync with source",
          op: "functions-integrations-sync",
          attributes: {
            shopId,
            integrator,
            apiToken,
            apiUrl,
            fullSync,
            fullReSync,
            apiPartnerId,
            apiTenantId,
          },
        },
        async () => {
          return fn;
        }
      );
    }
    return fn;
  },
  updateContainers: ({
    shopId,
    cardId,
    fromContainerIndex,
    fromContainerId,
    toContainerIndex,
    toContainerId,
  }: {
    shopId: string;
    cardId: string;
    fromContainerIndex: number;
    fromContainerId: string;
    toContainerIndex: number;
    toContainerId: string;
  }) => {
    const callable = httpsCallable(functions, "updateContainers");
    const fn = callable({
      shopId,
      tagId: cardId,
      from: {
        index: fromContainerIndex,
        container: fromContainerId,
      },
      to: {
        index: toContainerIndex,
        container: toContainerId,
      },
    });

    // Use random number to determine if to log the span
    if (Math.random() < SPAN_CAPTURE_RATE) {
      return sentry.startSpan(
        {
          name: "Update containers",
          op: "functions-updateContainers",
          attributes: {
            shopId,
            cardId,
            fromContainerIndex,
            fromContainerId,
            toContainerIndex,
            toContainerId,
          },
        },
        async () => {
          return fn;
        }
      );
    }
    return fn;
  },
  getServerTime: async (): Promise<number> => {
    const callable = httpsCallable<unknown, { serverTime: number }>(
      functions,
      "functionsV2-getServerTime"
    );
    const result = await callable();
    return result.data.serverTime;
  },
  updateUserClaims: ({
    userId,
    userRole,
    userRoleEnabled,
    userShopId,
    disableUser,
  }: {
    userId: string;
    userRole?: EmployeeRole;
    userRoleEnabled?: boolean;
    userShopId?: string;
    disableUser?: boolean;
  }) => {
    const callable = httpsCallable(functions, "functionsV2-updateUserClaims");
    return callable({
      userId,
      userRole,
      userRoleEnabled,
      userShopId,
      disableUser,
    });
  },
  getAuthUser: ({ email }: { email: string }) => {
    const callable = httpsCallable<{ email: string }, AuthUser>(
      functions,
      "functionsV2-getAuthUser"
    );
    return callable({ email });
  },
  createAuthUser: async ({
    email,
    displayName,
    shopId,
    employeeId,
    loginLink,
  }: {
    email: string;
    displayName: string;
    shopId: string;
    employeeId: string;
    loginLink: string;
  }) => {
    const callable = httpsCallable<
      {
        email: string;
        displayName: string;
        shopId: string;
        employeeId: string;
        loginLink: string;
      },
      AuthUser
    >(functions, "functionsV2-createAuthUser");
    return callable({ email, displayName, shopId, employeeId, loginLink });
  },
  adminSendLoginEmail: async ({
    email,
    displayName,
    shopId,
    employeeId,
    loginLink,
  }: {
    email: string;
    displayName: string;
    shopId: string;
    employeeId: string;
    loginLink: string;
  }) => {
    const callable = httpsCallable<
      {
        email: string;
        displayName: string;
        shopId: string;
        employeeId: string;
        loginLink: string;
      },
      AuthUser
    >(functions, "functionsV2-adminSendLoginEmail");
    return callable({ email, displayName, shopId, employeeId, loginLink });
  },
  deactivateAuthUser: ({ email }: { email: string }) => {
    const callable = httpsCallable<{ email: string }, AuthUser>(
      functions,
      "functionsV2-deactivateAuthUser"
    );
    return callable({ email });
  },
  reactivateAuthUser: ({
    shopId,
    employeeId,
  }: {
    shopId: string;
    employeeId: string;
  }) => {
    const callable = httpsCallable<
      { shopId: string; employeeId: string },
      AuthUser
    >(functions, "functionsV2-reactivateAuthUser");
    return callable({ shopId, employeeId });
  },
  setEmailVerified: ({
    email,
    emailVerified,
  }: {
    email: string;
    emailVerified: boolean;
  }) => {
    const callable = httpsCallable<
      { email: string; emailVerified: boolean },
      AuthUser
    >(functions, "functionsV2-setEmailVerified");
    return callable({ email, emailVerified });
  },
  moveCard: ({
    cardId,
    shopId,
    toContainerId,
    toIndex,
    chosenBidirectionalLabel,
    chosenTechnicianId,
  }: {
    cardId: string;
    shopId: string;
    toContainerId?: string;
    toIndex?: number;
    chosenBidirectionalLabel?: string;
    chosenTechnicianId?: string | null;
  }) => {
    const callable = httpsCallable<
      {
        cardId: string;
        shopId: string;
        toContainerId?: string;
        toIndex?: number;
        chosenBidirectionalLabel?: string;
        chosenTechnicianId?: string | null;
      },
      { technicianSyncFailed: boolean }
    >(functions, "cards-moveCard");
    return sentry.startSpan(
      {
        name: "Move card",
        op: "functions-cards-moveCard",
        attributes: {
          cardId,
          shopId,
          toContainerId,
          toIndex,
        },
      },
      async () => {
        return callable({
          cardId,
          shopId,
          toContainerId,
          toIndex,
          chosenBidirectionalLabel,
          chosenTechnicianId,
        });
      }
    );
  },
  syncOneRepairOrder: ({
    shopId,
    cardId,
  }: {
    shopId: string;
    cardId: string;
  }) => {
    const callable = httpsCallable(functions, "cards-syncOneRepairOrder");
    return sentry.startSpan(
      {
        name: "Sync one repair order",
        op: "functions-cards-syncOneRepairOrder",
        attributes: { shopId, cardId },
      },
      async () => {
        return callable({ shopId, cardId });
      }
    );
  },
  customerUpdateUpdate: ({
    shopId,
    customerUpdateId,
    customerUpdateData,
  }: {
    shopId: string;
    customerUpdateId: string;
    customerUpdateData: CustomerUpdate;
  }) => {
    const callable = httpsCallable(
      functions,
      "functionsV2-customerUpdateUpdate"
    );
    return callable({ shopId, customerUpdateId, customerUpdateData });
  },
  integratorStatus: (data: integratorCallableData) => {
    const callable = httpsCallable(functions, "integrations-status");
    return callable(data);
  },
  registerShop: (data: {
    email: string;
    shopName: string;
    website: string;
    phone: string;
    tenantId?: string;
    shopId?: string;
    integrator?: Integrators;
  }) => {
    const callable = httpsCallable<
      {
        email: string;
        shopName: string;
        website: string;
        phone: string;
        tenantId?: string;
        shopId?: string;
        integrator?: Integrators;
      },
      Shop
    >(functions, "functionsV2-registerShop");
    return callable(data);
  },
  adminCreateShop: (data: {
    email: string;
    shopName: string;
    integrator?: Integrators;
    sendActivationEmailNow: boolean;
    loginLink: string;
  }) => {
    const callable = httpsCallable<
      {
        email: string;
        shopName: string;
        integrator?: Integrators;
        sendActivationEmailNow: boolean;
        loginLink: string;
      },
      { shop: Shop; emailSent?: boolean }
    >(functions, "functionsV2-adminCreateShop");
    return callable(data);
  },
  adminReassignShopOwner: (data: AdminReassignShopOwnerRequest) => {
    const callable = httpsCallable<
      AdminReassignShopOwnerRequest,
      AdminReassignShopOwnerResult
    >(functions, "functionsV2-adminReassignShopOwner");
    return callable(data);
  },
  sendVerifyEmailEmail: (data: { uid: string; redirectLink: string }) => {
    const callable = httpsCallable(
      functions,
      "functionsV2-sendVerifyEmailEmail"
    );
    return callable(data);
  },
  sendPasswordResetEmail: (data: { email: string; redirectLink: string }) => {
    const callable = httpsCallable(
      functions,
      "functionsV2-sendPasswordResetEmail"
    );
    return callable(data);
  },
  sendInviteEmail: (data: { email: string; redirectLink: string }) => {
    const callable = httpsCallable(functions, "functionsV2-sendInviteEmail");
    return callable(data);
  },
  getStripeCustomer: () => {
    const callable = httpsCallable<never, { stripeCustomerId: string } | null>(
      functions,
      "functionsV2-getStripeCustomer"
    );
    return callable();
  },
  createStripeCustomer: (data: {
    shopId: string;
    shopName: string;
    firstName: string;
    lastName: string;
  }) => {
    const callable = httpsCallable<
      {
        shopId: string;
        shopName: string;
        firstName: string;
        lastName: string;
      },
      { stripeCustomerId: string }
    >(functions, "functionsV2-createStripeCustomer");
    return callable(data);
  },
  createCheckoutSession: (data: {
    shopId: string;
    customerId: string;
    priceIds: string[];
    successUrl: string;
    cancelUrl: string;
    couponId?: string;
  }) => {
    const callable = httpsCallable<
      {
        shopId: string;
        customerId: string;
        priceIds: string[];
        successUrl: string;
        cancelUrl: string;
        couponId?: string;
      },
      { sessionId: string; sessionUrl: string }
    >(functions, "functionsV2-createCheckoutSession");
    return callable(data);
  },
  createCustomerPortalSession: (data: {
    shopId?: string;
    returnUrl?: string;
  }) => {
    const callable = httpsCallable<
      {
        shopId?: string;
        returnUrl?: string;
      },
      { url: string }
    >(functions, "functionsV2-createCustomerPortalSession");
    return callable(data);
  },
  getLabels: (data: integratorCallableData) => {
    const callable = httpsCallable<
      integratorCallableData,
      { labels: string[] }
    >(functions, "integrations-syncLabels");
    return callable(data);
  },
  verifyPromotionCode: (data: {
    customerId?: string;
    promotionCode: string;
  }) => {
    const callable = httpsCallable<
      { customerId?: string; promotionCode: string },
      { codeValid: boolean; promotionCodes: StripePromotionCode[] }
    >(functions, "functionsV2-verifyPromotionCode");
    return callable(data);
  },
  updateCustomerUpdate: (data: UpdateCustomerUpdateData) => {
    const callable = httpsCallable<
      UpdateCustomerUpdateData,
      UpdateCustomerUpdateResponse
    >(functions, "functionsV2-updateCustomerUpdate");
    return callable(data);
  },
  getAvailableUpdateTime: (data: {
    shopId: string;
    userId: string;
    desiredUpdateTime: Date;
  }) => {
    const callable = httpsCallable<
      { shopId: string; userId: string; desiredUpdateTime: Date },
      string | null
    >(functions, "functionsV2-getAvailableUpdateTime");
    return callable(data);
  },
  computeContainerTimeMetric: (data: {
    shopId: string;
    configuration: any;
  }) => {
    const callable = httpsCallable(
      functions,
      "functionsV2-computeContainerTimeMetric"
    );
    return callable(data);
  },
  reconcileLabelSyncInFlight: (data: { shopId: string }) => {
    const callable = httpsCallable<
      { shopId: string },
      { shopId: string; inFlightCount: number }
    >(functions, "functionsV2-reconcileLabelSyncInFlight");
    return callable(data);
  },
};
